import React, { useState, useEffect, useMemo } from 'react';
import {
  BookOpen,
  Check,
  X,
  Sparkles,
  Printer,
  ChevronLeft,
  ChevronRight,
  Zap,
  FileText,
  Layers,
  Lock,
  Clock,
  RefreshCw,
  PartyPopper,
  ArrowLeft,
} from 'lucide-react';
import { PraxisPaper, Flashcard, FlashcardMastery, UserProfile } from '../types';
import { PRAXIS_PAPERS } from '../data/praxisPapers';
import {
  getPaperCatalogSummaries,
  isPaperUnlockedForUser,
  getFlashcardsForPaperFromFirestore,
  getPersonalFlashcardsForUser,
  getUserFlashcardMastery,
  recordFlashcardReview,
} from '../lib/firebase';

interface StudyResourcesProps {
  paperId: string;
  onStartTest: (paperId: string) => void;
  userProfile?: UserProfile;
  openPricingModal?: () => void;
  onBack?: () => void;
}

// --- Domain color system for the Flashcard Deck -------------------------
// Every card's accent color is derived deterministically from its `domain`
// string, so the same domain (e.g. "Mathematics") always renders the same
// color everywhere it appears, across papers and across sessions -- no
// stored color field needed. All class names below are written out in full
// (never built with template-literal interpolation) so Tailwind's JIT scan
// picks every one of them up at build time.
interface DomainColorScheme {
  topBar: string;
  badgeBg: string;
  badgeText: string;
  badgeBorder: string;
  cardBorder: string;
  cardBorderHover: string;
  labelText: string;
  dotActive: string;
  dotInactive: string;
  backBg: string;
}

const DOMAIN_COLOR_PALETTE: DomainColorScheme[] = [
  {
    topBar: 'bg-indigo-500',
    badgeBg: 'bg-indigo-50',
    badgeText: 'text-indigo-700',
    badgeBorder: 'border-indigo-200',
    cardBorder: 'border-indigo-200',
    cardBorderHover: 'hover:border-indigo-300',
    labelText: 'text-indigo-600',
    dotActive: 'bg-indigo-500',
    dotInactive: 'bg-indigo-200',
    backBg: 'bg-indigo-50/40',
  },
  {
    topBar: 'bg-emerald-500',
    badgeBg: 'bg-emerald-50',
    badgeText: 'text-emerald-700',
    badgeBorder: 'border-emerald-200',
    cardBorder: 'border-emerald-200',
    cardBorderHover: 'hover:border-emerald-300',
    labelText: 'text-emerald-600',
    dotActive: 'bg-emerald-500',
    dotInactive: 'bg-emerald-200',
    backBg: 'bg-emerald-50/40',
  },
  {
    topBar: 'bg-amber-500',
    badgeBg: 'bg-amber-50',
    badgeText: 'text-amber-800',
    badgeBorder: 'border-amber-200',
    cardBorder: 'border-amber-200',
    cardBorderHover: 'hover:border-amber-300',
    labelText: 'text-amber-700',
    dotActive: 'bg-amber-500',
    dotInactive: 'bg-amber-200',
    backBg: 'bg-amber-50/40',
  },
  {
    topBar: 'bg-rose-500',
    badgeBg: 'bg-rose-50',
    badgeText: 'text-rose-700',
    badgeBorder: 'border-rose-200',
    cardBorder: 'border-rose-200',
    cardBorderHover: 'hover:border-rose-300',
    labelText: 'text-rose-600',
    dotActive: 'bg-rose-500',
    dotInactive: 'bg-rose-200',
    backBg: 'bg-rose-50/40',
  },
  {
    topBar: 'bg-sky-500',
    badgeBg: 'bg-sky-50',
    badgeText: 'text-sky-700',
    badgeBorder: 'border-sky-200',
    cardBorder: 'border-sky-200',
    cardBorderHover: 'hover:border-sky-300',
    labelText: 'text-sky-600',
    dotActive: 'bg-sky-500',
    dotInactive: 'bg-sky-200',
    backBg: 'bg-sky-50/40',
  },
  {
    topBar: 'bg-violet-500',
    badgeBg: 'bg-violet-50',
    badgeText: 'text-violet-700',
    badgeBorder: 'border-violet-200',
    cardBorder: 'border-violet-200',
    cardBorderHover: 'hover:border-violet-300',
    labelText: 'text-violet-600',
    dotActive: 'bg-violet-500',
    dotInactive: 'bg-violet-200',
    backBg: 'bg-violet-50/40',
  },
  {
    topBar: 'bg-fuchsia-500',
    badgeBg: 'bg-fuchsia-50',
    badgeText: 'text-fuchsia-700',
    badgeBorder: 'border-fuchsia-200',
    cardBorder: 'border-fuchsia-200',
    cardBorderHover: 'hover:border-fuchsia-300',
    labelText: 'text-fuchsia-600',
    dotActive: 'bg-fuchsia-500',
    dotInactive: 'bg-fuchsia-200',
    backBg: 'bg-fuchsia-50/40',
  },
  {
    topBar: 'bg-teal-500',
    badgeBg: 'bg-teal-50',
    badgeText: 'text-teal-700',
    badgeBorder: 'border-teal-200',
    cardBorder: 'border-teal-200',
    cardBorderHover: 'hover:border-teal-300',
    labelText: 'text-teal-600',
    dotActive: 'bg-teal-500',
    dotInactive: 'bg-teal-200',
    backBg: 'bg-teal-50/40',
  },
  {
    topBar: 'bg-orange-500',
    badgeBg: 'bg-orange-50',
    badgeText: 'text-orange-700',
    badgeBorder: 'border-orange-200',
    cardBorder: 'border-orange-200',
    cardBorderHover: 'hover:border-orange-300',
    labelText: 'text-orange-600',
    dotActive: 'bg-orange-500',
    dotInactive: 'bg-orange-200',
    backBg: 'bg-orange-50/40',
  },
  {
    topBar: 'bg-cyan-500',
    badgeBg: 'bg-cyan-50',
    badgeText: 'text-cyan-700',
    badgeBorder: 'border-cyan-200',
    cardBorder: 'border-cyan-200',
    cardBorderHover: 'hover:border-cyan-300',
    labelText: 'text-cyan-600',
    dotActive: 'bg-cyan-500',
    dotInactive: 'bg-cyan-200',
    backBg: 'bg-cyan-50/40',
  },
];

function getDomainColors(domain: string | undefined): DomainColorScheme {
  const key = domain || 'General';
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return DOMAIN_COLOR_PALETTE[hash % DOMAIN_COLOR_PALETTE.length];
}

// Small clickable progress-dot strip for a flashcard deck, each dot tinted
// by that card's own domain color so the strip doubles as a mini map of
// which topics are coming up. `onJump` is omitted for a session (like the
// Due Today review queue) that shouldn't support skipping ahead.
const DeckProgressDots: React.FC<{
  cards: Flashcard[];
  currentIndex: number;
  onJump?: (index: number) => void;
}> = ({ cards, currentIndex, onJump }) => {
  if (cards.length <= 1) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-1.5 px-2">
      {cards.map((card, idx) => {
        const colors = getDomainColors(card.domain);
        const isActive = idx === currentIndex;
        return (
          <button
            key={card.id || idx}
            type="button"
            onClick={onJump ? () => onJump(idx) : undefined}
            disabled={!onJump}
            title={card.domain}
            className={`rounded-full transition-all ${
              isActive
                ? `w-5 h-2 ${colors.dotActive}`
                : `w-2 h-2 ${colors.dotInactive} ${onJump ? 'hover:opacity-70' : ''}`
            } ${onJump ? 'cursor-pointer' : 'cursor-default'}`}
          />
        );
      })}
    </div>
  );
};

// Formerly the "Flashcard Deck", "High-Yield Summaries" and "Printable Study
// Sheet" tabs inside the old dropdown-based StudyGuides.tsx. Relocated here,
// under each paper's own practice/test entry point (PaperTabs.tsx's paper
// card), now that Study Guides itself is the exam-family -> tile grid ->
// PDF viewer flow. Logic is otherwise unchanged from the original component.
export const StudyResources: React.FC<StudyResourcesProps> = ({
  paperId,
  onStartTest,
  userProfile,
  openPricingModal,
  onBack,
}) => {
  const [papers, setPapers] = useState<PraxisPaper[]>(PRAXIS_PAPERS);
  const [activeTab, setActiveTab] = useState<'highyield' | 'flashcards' | 'print'>(
    'highyield',
  );

  useEffect(() => {
    async function loadPapersData() {
      try {
        const fp = await getPaperCatalogSummaries();
        if (fp && fp.length > 0) setPapers(fp);
      } catch (err) {
        console.warn('StudyResources: Could not load papers from Firestore:', err);
      }
    }
    loadPapersData();
  }, []);

  // --- FLASHCARD DECK STATE (shared paper deck + this student's personal deck) ---
  const [sharedCards, setSharedCards] = useState<Flashcard[]>([]);
  const [personalCards, setPersonalCards] = useState<Flashcard[]>([]);
  const [flashcardMastery, setFlashcardMastery] = useState<
    Record<string, FlashcardMastery>
  >({});
  const [loadingFlashcards, setLoadingFlashcards] = useState<boolean>(false);
  const [deckMode, setDeckMode] = useState<'due' | 'browse'>('due');

  // Load the real flashcard subcollections for this paper. Shared cards come
  // from praxisPapers/{paperId}/flashcards; personal cards (auto-generated
  // from this student's own missed questions) come from users/{uid}/flashcards.
  useEffect(() => {
    async function loadFlashcardData() {
      if (!paperId) return;
      setLoadingFlashcards(true);
      try {
        const shared = await getFlashcardsForPaperFromFirestore(paperId);
        setSharedCards(shared);

        if (userProfile?.uid) {
          const [personal, mastery] = await Promise.all([
            getPersonalFlashcardsForUser(userProfile.uid, paperId),
            getUserFlashcardMastery(userProfile.uid),
          ]);
          setPersonalCards(personal);
          const masteryMap: Record<string, FlashcardMastery> = {};
          mastery.forEach((m) => {
            masteryMap[m.flashcardId] = m;
          });
          setFlashcardMastery(masteryMap);
        } else {
          setPersonalCards([]);
          setFlashcardMastery({});
        }
      } catch (err) {
        console.warn('StudyResources: Error loading flashcard deck:', err);
      } finally {
        setLoadingFlashcards(false);
      }
    }
    loadFlashcardData();
  }, [paperId, userProfile?.uid]);

  // Combined deck this student sees: the shared paper deck plus their own
  // private mistake-driven cards.
  const combinedCards = useMemo<Flashcard[]>(
    () => [...sharedCards, ...personalCards],
    [sharedCards, personalCards],
  );

  // Cards due for spaced-repetition review right now: never-reviewed cards
  // (no mastery record yet) plus cards whose nextReviewDue has passed.
  const dueCards = useMemo<Flashcard[]>(() => {
    const now = Date.now();
    return combinedCards.filter((fc) => {
      const m = flashcardMastery[fc.id];
      if (!m || !m.nextReviewDue) return true;
      return new Date(m.nextReviewDue).getTime() <= now;
    });
  }, [combinedCards, flashcardMastery]);

  // --- Browse Mode (flip-through, session-local "mastered" tracking only) ---
  const [cardIndex, setCardIndex] = useState<number>(0);
  const [isFlipped, setIsFlipped] = useState<boolean>(false);
  const [masteredCards, setMasteredCards] = useState<Record<string, boolean>>({});

  const paper = papers.find((p) => p.id === paperId || p.code === paperId) || papers[0];
  const currentCard: Flashcard | undefined = combinedCards[cardIndex];

  const handleNextCard = () => {
    setIsFlipped(false);
    setCardIndex((prev) => (prev + 1) % combinedCards.length);
  };

  const handlePrevCard = () => {
    setIsFlipped(false);
    setCardIndex((prev) => (prev - 1 + combinedCards.length) % combinedCards.length);
  };

  const handleJumpToCard = (idx: number) => {
    setIsFlipped(false);
    setCardIndex(idx);
  };

  const markMastered = (cardId: string, isMastered: boolean) => {
    setMasteredCards((prev) => ({ ...prev, [cardId]: isMastered }));
    handleNextCard();
  };

  // --- Due-Today Review Session (real spaced repetition, backed by Firestore) ---
  const [reviewQueue, setReviewQueue] = useState<Flashcard[]>([]);
  const [reviewIndex, setReviewIndex] = useState<number>(0);
  const [reviewFlipped, setReviewFlipped] = useState<boolean>(false);
  const [reviewStats, setReviewStats] = useState<{ correct: number; incorrect: number }>({
    correct: 0,
    incorrect: 0,
  });
  const [reviewSubmitting, setReviewSubmitting] = useState<boolean>(false);

  const handleStartReviewSession = () => {
    const shuffled = [...dueCards];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setReviewQueue(shuffled);
    setReviewIndex(0);
    setReviewFlipped(false);
    setReviewStats({ correct: 0, incorrect: 0 });
  };

  const handleGradeReviewCard = async (gotItRight: boolean) => {
    const card = reviewQueue[reviewIndex];
    if (!card || !userProfile?.uid || reviewSubmitting) return;
    setReviewSubmitting(true);
    try {
      await recordFlashcardReview(userProfile.uid, {
        flashcardId: card.id,
        paperId: card.paperId,
        domain: card.domain,
        scope: card.scope || 'shared',
        gotItRight,
        timestamp: new Date().toISOString(),
      });
      setReviewStats((prev) => ({
        correct: prev.correct + (gotItRight ? 1 : 0),
        incorrect: prev.incorrect + (gotItRight ? 0 : 1),
      }));
    } catch (err) {
      console.warn('Error recording flashcard review:', err);
    } finally {
      setReviewSubmitting(false);
      setReviewFlipped(false);
      setReviewIndex((prev) => prev + 1);
    }
  };

  const handleFinishReviewSession = async () => {
    setReviewQueue([]);
    setReviewIndex(0);
    setReviewFlipped(false);
    // Refresh mastery so the "due today" count reflects this session's results.
    if (userProfile?.uid) {
      try {
        const mastery = await getUserFlashcardMastery(userProfile.uid);
        const masteryMap: Record<string, FlashcardMastery> = {};
        mastery.forEach((m) => {
          masteryMap[m.flashcardId] = m;
        });
        setFlashcardMastery(masteryMap);
      } catch (err) {
        console.warn('Error refreshing flashcard mastery:', err);
      }
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-8 print:py-0 print:px-0 print:max-w-none">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-200 print:hidden">
        <div>
          {onBack && (
            <button
              onClick={onBack}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition mb-2"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Exam Papers
            </button>
          )}
          <div className="flex items-center gap-2 text-indigo-600 font-bold text-xs uppercase tracking-wider">
            <BookOpen className="w-4 h-4" /> Study Resources
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">
            PRAXIS {paper.code}: {paper.title}
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            High-yield summaries, flashcard drills, and a printable cheat sheet for this paper.
          </p>
        </div>
      </div>

      {/* Sub-Nav Tabs */}
      <div className="flex items-center justify-between border-b border-slate-200 print:hidden">
        <div className="flex gap-2">
          {[
            { id: 'highyield', label: 'High-Yield Summaries', icon: Zap },
            {
              id: 'flashcards',
              label: `Flashcard Deck (${combinedCards.length})`,
              icon: Layers,
            },
            { id: 'print', label: 'Printable Study Sheet', icon: FileText },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-3 text-xs sm:text-sm font-bold border-b-2 transition ${
                  isActive
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'print' && (
          <button
            onClick={handlePrint}
            className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs px-3.5 py-1.5 rounded-xl transition flex items-center gap-1.5"
          >
            <Printer className="w-3.5 h-3.5" /> Print / Save PDF
          </button>
        )}
      </div>

      {/* Subscription Paywall Gate */}
      {userProfile &&
      !isPaperUnlockedForUser(userProfile, paper.code) &&
      !isPaperUnlockedForUser(userProfile, paper.id) ? (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-xs p-8 sm:p-12 text-center space-y-6 max-w-3xl mx-auto">
          <div className="inline-flex p-4 rounded-2xl bg-amber-50 text-amber-600 border border-amber-200">
            <Lock className="w-10 h-10" />
          </div>
          <div className="space-y-2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 uppercase tracking-wide">
              Subscription Required
            </div>
            <h2 className="text-2xl font-black text-slate-900">
              Unlock Full Study Resources
            </h2>
            <p className="text-sm sm:text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
              High-yield concept summaries, active-recall flashcard drills, and printable
              study sheets for{' '}
              <strong className="text-slate-900">
                {paper.title} ({paper.code})
              </strong>{' '}
              require an active PraxisPrep Pro subscription.
            </p>
          </div>

          <div className="flex justify-center pt-2">
            {openPricingModal && (
              <button
                onClick={openPricingModal}
                className="px-6 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-sm transition shadow-xs flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4 text-amber-300" /> Unlock with Subscription
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* TAB 1: HIGH YIELD SUMMARIES */}
          {activeTab === 'highyield' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(paper.highYieldTopics || []).map((topic, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs hover:border-indigo-200 transition space-y-4"
                  >
                    <div className="flex items-center justify-between">
                      <span className="px-2.5 py-1 rounded-full text-xs font-extrabold bg-indigo-50 text-indigo-700 border border-indigo-200">
                        {topic.domain}
                      </span>
                      <span className="text-xs font-semibold text-amber-600 flex items-center gap-1">
                        <Sparkles className="w-3.5 h-3.5" /> High Frequency Topic
                      </span>
                    </div>

                    <h3 className="text-lg font-bold text-slate-900">{topic.title}</h3>
                    <p className="text-xs text-slate-600 leading-relaxed">{topic.summary}</p>

                    <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-100 space-y-2 text-xs text-slate-700">
                      <div className="font-bold text-slate-900 uppercase tracking-wider text-[10px]">
                        Key Takeaways
                      </div>
                      <ul className="list-disc list-inside space-y-1">
                        {(topic.keyPoints || []).map((kp, kIdx) => (
                          <li key={kIdx}>{kp}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-amber-50 p-3 rounded-xl border border-amber-200/80 text-xs text-amber-900 font-medium">
                      <strong>Exam Tip: </strong> {topic.examTip}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-center pt-4">
                <button
                  onClick={() => onStartTest(paper.id)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-6 py-3 rounded-xl transition shadow-md"
                >
                  Test Knowledge on PRAXIS {paper.code} Exam &rarr;
                </button>
              </div>
            </div>
          )}

          {/* TAB 2: FLASHCARD DECK */}
          {activeTab === 'flashcards' && (
            <div className="max-w-2xl mx-auto space-y-6">
              {/* Due Today / Browse All sub-mode toggle */}
              <div className="flex items-center justify-center gap-2 bg-slate-100 p-1 rounded-xl w-full sm:w-auto sm:mx-auto">
                <button
                  onClick={() => setDeckMode('due')}
                  className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition ${
                    deckMode === 'due'
                      ? 'bg-white text-indigo-600 shadow-xs'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Clock className="w-3.5 h-3.5" /> Due Today ({dueCards.length})
                </button>
                <button
                  onClick={() => setDeckMode('browse')}
                  className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition ${
                    deckMode === 'browse'
                      ? 'bg-white text-indigo-600 shadow-xs'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Layers className="w-3.5 h-3.5" /> Browse All ({combinedCards.length})
                </button>
              </div>

              {loadingFlashcards ? (
                <div className="text-center py-12 text-slate-500 text-sm">
                  Loading your flashcard deck...
                </div>
              ) : deckMode === 'due' ? (
                /* --- DUE TODAY: real spaced-repetition review session --- */
                reviewQueue.length > 0 && reviewIndex < reviewQueue.length ? (
                  (() => {
                    const liveCard = reviewQueue[reviewIndex];
                    const colors = getDomainColors(liveCard.domain);
                    return (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between text-xs font-bold text-slate-500">
                          <span>
                            Reviewing {reviewIndex + 1} of {reviewQueue.length}
                          </span>
                          <span>
                            <span className="text-emerald-600">{reviewStats.correct} got it</span>
                            {' · '}
                            <span className="text-rose-600">{reviewStats.incorrect} missed</span>
                          </span>
                        </div>

                        <DeckProgressDots cards={reviewQueue} currentIndex={reviewIndex} />

                        <div
                          onClick={() => setReviewFlipped(!reviewFlipped)}
                          className="group [perspective:1500px] w-full min-h-[300px] cursor-pointer select-none"
                        >
                          <div
                            className={`relative w-full min-h-[300px] transition-transform duration-500 [transform-style:preserve-3d] ${
                              reviewFlipped ? '[transform:rotateY(180deg)]' : ''
                            }`}
                          >
                            {/* Front face: Term */}
                            <div
                              className={`absolute inset-0 [backface-visibility:hidden] bg-white rounded-3xl border-2 ${colors.cardBorder} ${colors.cardBorderHover} p-8 shadow-md group-hover:shadow-xl transition-shadow flex flex-col justify-between overflow-hidden`}
                            >
                              <div className={`absolute top-0 left-0 right-0 h-1.5 ${colors.topBar}`} />
                              <div className="flex items-center justify-between text-xs font-bold pt-1">
                                <span
                                  className={`px-2.5 py-1 rounded-full ${colors.badgeBg} ${colors.labelText} border ${colors.badgeBorder}`}
                                >
                                  {liveCard.domain}
                                </span>
                                <span
                                  className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
                                    liveCard.scope === 'personal'
                                      ? 'bg-amber-100 text-amber-800'
                                      : 'bg-slate-100 text-slate-600'
                                  }`}
                                >
                                  {liveCard.scope === 'personal' ? 'Your Mistake' : 'Shared Deck'}
                                </span>
                              </div>

                              <div className="my-auto text-center py-6">
                                <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-2">
                                  Term
                                </span>
                                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                                  {liveCard.term}
                                </h2>
                              </div>

                              <div className="text-center text-[11px] text-slate-400 font-medium">
                                Click card to flip ↺
                              </div>
                            </div>

                            {/* Back face: Definition */}
                            <div
                              className={`absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] ${colors.backBg} rounded-3xl border-2 ${colors.cardBorder} p-8 shadow-md flex flex-col justify-between overflow-hidden`}
                            >
                              <div className={`absolute top-0 left-0 right-0 h-1.5 ${colors.topBar}`} />
                              <div className={`flex items-center justify-between text-xs font-bold ${colors.labelText} pt-1`}>
                                <span>{liveCard.domain}</span>
                              </div>

                              <div className="my-auto text-center py-6 space-y-3">
                                <span className={`text-xs font-bold uppercase tracking-wider ${colors.labelText} block`}>
                                  Definition &amp; Application
                                </span>
                                <p className="text-base font-semibold text-slate-800 leading-relaxed">
                                  {liveCard.definition}
                                </p>
                                {liveCard.exampleScenario && (
                                  <p className="text-xs text-slate-600 bg-white/70 p-3 rounded-xl border border-slate-200/60">
                                    <strong>Classroom Example: </strong> {liveCard.exampleScenario}
                                  </p>
                                )}
                              </div>

                              <div className="text-center text-[11px] text-slate-400 font-medium">
                                Showing Definition
                              </div>
                            </div>
                          </div>
                        </div>

                        {reviewFlipped ? (
                          <div className="flex items-center justify-center gap-3">
                            <button
                              onClick={() => handleGradeReviewCard(false)}
                              disabled={reviewSubmitting}
                              className="bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 font-bold text-xs px-5 py-3 rounded-xl transition flex items-center gap-1.5 disabled:opacity-50"
                            >
                              <X className="w-4 h-4" /> Missed It
                            </button>
                            <button
                              onClick={() => handleGradeReviewCard(true)}
                              disabled={reviewSubmitting}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-5 py-3 rounded-xl transition shadow-xs flex items-center gap-1.5 disabled:opacity-50"
                            >
                              <Check className="w-4 h-4" /> Got It
                            </button>
                          </div>
                        ) : (
                          <p className="text-center text-xs text-slate-400">
                            Flip the card, then grade yourself honestly.
                          </p>
                        )}
                      </div>
                    );
                  })()
                ) : reviewStats.correct + reviewStats.incorrect > 0 ? (
                  /* Session just finished */
                  <div className="text-center py-12 space-y-4">
                    <div className="inline-flex p-4 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-200">
                      <PartyPopper className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-extrabold text-slate-900">
                      Review Session Complete!
                    </h3>
                    <p className="text-sm text-slate-600">
                      {reviewStats.correct} got it &middot; {reviewStats.incorrect} missed
                    </p>
                    <button
                      onClick={handleFinishReviewSession}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-5 py-3 rounded-xl transition shadow-xs inline-flex items-center gap-1.5"
                    >
                      <RefreshCw className="w-4 h-4" /> Back to Deck
                    </button>
                  </div>
                ) : !userProfile ? (
                  <div className="text-center py-12 text-slate-500 text-sm">
                    Sign in to track spaced-repetition progress on your flashcard deck.
                  </div>
                ) : dueCards.length === 0 ? (
                  <div className="text-center py-12 space-y-2">
                    <div className="inline-flex p-4 rounded-2xl bg-slate-100 text-slate-400">
                      <Check className="w-8 h-8" />
                    </div>
                    <p className="text-sm text-slate-500">
                      Nothing due right now &mdash; you're all caught up!
                    </p>
                    <p className="text-xs text-slate-400">
                      Check back later, or browse the full deck any time.
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-12 space-y-4">
                    <p className="text-sm text-slate-600">
                      <span className="font-extrabold text-slate-900">{dueCards.length}</span>{' '}
                      card{dueCards.length === 1 ? '' : 's'} due for review right now.
                    </p>
                    <button
                      onClick={handleStartReviewSession}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm px-6 py-3 rounded-xl transition shadow-md inline-flex items-center gap-2"
                    >
                      <Sparkles className="w-4 h-4" /> Start Review Session
                    </button>
                  </div>
                )
              ) : /* --- BROWSE ALL: original flip-through deck, session-local mastery only --- */
              combinedCards.length > 0 && currentCard ? (
                (() => {
                  const colors = getDomainColors(currentCard.domain);
                  return (
                    <div className="space-y-6">
                      {/* Progress Indicator */}
                      <div className="flex items-center justify-between text-xs font-bold text-slate-500">
                        <span>
                          Card {cardIndex + 1} of {combinedCards.length}
                        </span>
                        <span>
                          Mastered: {Object.values(masteredCards).filter(Boolean).length} /{' '}
                          {combinedCards.length}
                        </span>
                      </div>

                      <DeckProgressDots
                        cards={combinedCards}
                        currentIndex={cardIndex}
                        onJump={handleJumpToCard}
                      />

                      {/* Interactive Flip Card Container */}
                      <div
                        onClick={() => setIsFlipped(!isFlipped)}
                        className="group [perspective:1500px] w-full min-h-[300px] cursor-pointer select-none"
                      >
                        <div
                          className={`relative w-full min-h-[300px] transition-transform duration-500 [transform-style:preserve-3d] ${
                            isFlipped ? '[transform:rotateY(180deg)]' : ''
                          }`}
                        >
                          {/* Front face: Term */}
                          <div
                            className={`absolute inset-0 [backface-visibility:hidden] bg-white rounded-3xl border-2 ${colors.cardBorder} ${colors.cardBorderHover} p-8 shadow-md group-hover:shadow-xl transition-shadow flex flex-col justify-between overflow-hidden`}
                          >
                            <div className={`absolute top-0 left-0 right-0 h-1.5 ${colors.topBar}`} />
                            <div className="flex items-center justify-between text-xs font-bold pt-1">
                              <span
                                className={`px-2.5 py-1 rounded-full ${colors.badgeBg} ${colors.labelText} border ${colors.badgeBorder}`}
                              >
                                {currentCard.domain}
                              </span>
                              <span className="text-slate-400 font-normal text-[11px]">
                                Click card to flip ↺
                              </span>
                            </div>

                            <div className="my-auto text-center py-6">
                              <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-2">
                                Term
                              </span>
                              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                                {currentCard.term}
                              </h2>
                            </div>

                            <div className="text-center text-[11px] text-slate-400 font-medium">
                              Showing Term
                            </div>
                          </div>

                          {/* Back face: Definition */}
                          <div
                            className={`absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] ${colors.backBg} rounded-3xl border-2 ${colors.cardBorder} p-8 shadow-md flex flex-col justify-between overflow-hidden`}
                          >
                            <div className={`absolute top-0 left-0 right-0 h-1.5 ${colors.topBar}`} />
                            <div className={`flex items-center justify-between text-xs font-bold ${colors.labelText} pt-1`}>
                              <span>{currentCard.domain}</span>
                            </div>

                            <div className="my-auto text-center py-6 space-y-3">
                              <span className={`text-xs font-bold uppercase tracking-wider ${colors.labelText} block`}>
                                Definition &amp; Application
                              </span>
                              <p className="text-base font-semibold text-slate-800 leading-relaxed">
                                {currentCard.definition}
                              </p>
                              {currentCard.exampleScenario && (
                                <p className="text-xs text-slate-600 bg-white/70 p-3 rounded-xl border border-slate-200/60">
                                  <strong>Classroom Example: </strong> {currentCard.exampleScenario}
                                </p>
                              )}
                            </div>

                            <div className="text-center text-[11px] text-slate-400 font-medium">
                              Showing Definition
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Navigation & Session-Local Mastery Buttons */}
                      <div className="flex items-center justify-between gap-4">
                        <button
                          onClick={handlePrevCard}
                          className="p-3 rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-100 transition"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>

                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => markMastered(currentCard.id, false)}
                            className="bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 font-bold text-xs px-4 py-3 rounded-xl transition flex items-center gap-1.5"
                          >
                            <X className="w-4 h-4" /> Need Review
                          </button>

                          <button
                            onClick={() => markMastered(currentCard.id, true)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-5 py-3 rounded-xl transition shadow-xs flex items-center gap-1.5"
                          >
                            <Check className="w-4 h-4" /> Got It Mastered!
                          </button>
                        </div>

                        <button
                          onClick={handleNextCard}
                          className="p-3 rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-100 transition"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="text-center py-12 text-slate-500 text-sm">
                  No flashcards available for this paper yet.
                </div>
              )}
            </div>
          )}

          {/* TAB 3: PRINTABLE STUDY SHEET */}
          {activeTab === 'print' && (
            <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-xs space-y-8 print:border-none print:shadow-none print:p-0">
              <div className="border-b border-slate-200 pb-4 text-center space-y-1">
                <h2 className="text-2xl font-extrabold text-slate-900">
                  PRAXIS {paper.code}: {paper.title} Study Cheat Sheet
                </h2>
                <p className="text-xs text-slate-500">Official State Standard Prep Summary Sheet</p>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="font-bold text-slate-900 text-base mb-2">
                    1. High-Yield Concepts
                  </h3>
                  <div className="space-y-3">
                    {(paper.highYieldTopics || []).map((t, idx) => (
                      <div
                        key={idx}
                        className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs space-y-1"
                      >
                        <div className="font-bold text-indigo-700">
                          {t.title} ({t.domain})
                        </div>
                        <p>{t.summary}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-slate-900 text-base mb-2">
                    2. Key Terminology & Definitions
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    {sharedCards.map((fc, idx) => (
                      <div key={idx} className="p-3 border border-slate-200 rounded-xl">
                        <strong className="text-slate-900 block">{fc.term}</strong>
                        <span className="text-slate-600">{fc.definition}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
